// Story ▸ Glossary (GlossaryViews.swift): one list of the names and words the story uses — the cast's names and aliases,
// the words added here, and every word with a pronunciation — each with its kind, how it's said ("Say as", which
// audiobooks and Read Aloud use) and a note. Add Word, or choose a word to change its kind, other spellings or note (a
// name from the cast opens it in Cast & Places). "Not in the dictionary" needs the platform's spell checker, which a
// browser doesn't lend a page, and the writer's own dictionary lives in iCloud: both are on the Mac and iPad.
import { useState } from 'react';
import {
  GLOSSARY_KIND_LABELS, GLOSSARY_KINDS, glossaryEntry, glossaryRows, readGlossary, readPronunciations,
  type GlossaryEntry, type GlossaryKind, type GlossaryRow,
} from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { announce } from '../../shell/context.ts';
import { navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import type { LiveProject } from '../../store/project.ts';
import { opened, storyEdit } from '../../store/story.ts';
import { CommitInput } from '../FieldEditor.tsx';
import { ConfirmDialog } from '../Menu.tsx';
import { plural, StorySheet } from './common.tsx';

export function GlossaryPage({ project, live, hrefFor }: PageProps) {
  const ws = useWorkspace();
  const canWrite = !!ws?.canWrite && !!live;
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ entry: GlossaryEntry; isNew: boolean } | null>(null);
  const p = opened(project);
  const entries = readGlossary(p.record);
  const pronunciations = readPronunciations(p.record);
  const all = glossaryRows(entries, p.record.entities, pronunciations);
  const q = search.trim().toLowerCase();
  const rows = q ? all.filter((r) => r.term.toLowerCase().includes(q) || r.note.toLowerCase().includes(q)) : all;
  const open = (row: GlossaryRow) => {
    if (row.source.type === 'entity') navigate(hrefFor('plan/cast', null, { who: row.source.id }));
    else if (row.source.type === 'entry') {
      const id = row.source.id;
      const entry = entries.find((e) => e.id === id);
      if (entry) setEditing({ entry, isNew: false });
    } else setEditing({ entry: glossaryEntry(row.term), isNew: true });
  };
  return (
    <div className="story-page glossary-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Glossary</h1>
        {canWrite ? (
          <button type="button" className="button primary small" onClick={() => setEditing({ entry: glossaryEntry(''), isNew: true })}>
            Add Word
          </button>
        ) : null}
      </header>
      <p className="lede glossary-lede">
        Names and words your story uses. They’re spelled right everywhere, names are offered when you type @, and Say as is how audiobooks and Read Aloud pronounce them.
      </p>
      <section className="glossary-card" aria-labelledby="not-in-dictionary">
        <p className="glossary-card-head">
          <span id="not-in-dictionary" className="eyebrow accent">Not in the dictionary</span>
        </p>
        <p className="apple-note" data-apple-note="Not in the dictionary">
          <strong>On your Mac or iPad:</strong> the words in the manuscript the spell checker doesn’t know, to add in one go. A browser doesn’t let a page use its spell
          checker, so here add words with Add Word. Your own dictionary for every project is in Settings ▸ Author ▸ My dictionary there, and syncs through iCloud.
        </p>
      </section>
      <div className="glossary-bar">
        <p className="eyebrow" data-glossary-rows={all.length}>
          {plural(all.length, 'word')}
        </p>
        <label>
          <span className="visually-hidden">Search the glossary</span>
          <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {rows.length ? (
        <ul className="glossary-list">
          {rows.map((row) => (
            <GlossaryRowView key={row.id} row={row} live={live} canWrite={canWrite} onOpen={() => open(row)} />
          ))}
        </ul>
      ) : (
        <p className="empty-note">{q ? `Nothing matches “${search.trim()}”.` : 'No words yet. Add the names and invented words your story uses.'}</p>
      )}
      {editing ? (
        <EntryEditor
          entry={editing.entry}
          isNew={editing.isNew}
          sayAs={editing.entry.term ? (all.find((r) => r.term.toLowerCase() === editing.entry.term.toLowerCase())?.sayAs ?? '') : ''}
          live={live}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function GlossaryRowView({ row, live, canWrite, onOpen }: { row: GlossaryRow; live: LiveProject | null; canWrite: boolean; onOpen: () => void }) {
  const help = row.source.type === 'entity' ? 'Show in Cast & Places' : row.source.type === 'entry' ? 'Edit this word' : 'Add to the glossary';
  return (
    <li className="glossary-row" data-term={row.term} data-source={row.source.type}>
      <button type="button" className="glossary-term" title={help} onClick={onOpen} disabled={!canWrite && row.source.type !== 'entity'}>
        <span className="glossary-word">{row.term}</span>
        <span className="glossary-kind">{row.aliasOf ? `${row.kindLabel} · also ${row.aliasOf}` : row.kindLabel}</span>
      </button>
      <span className="glossary-say">
        <span aria-hidden="true" className="say-glyph">Say</span>
        {canWrite ? (
          <CommitInput
            inline
            className="field-input inline say-as"
            label={`Say ${row.term} as`}
            value={row.sayAs ?? ''}
            onCommit={(t) => {
              if (live) storyEdit(live, (e) => e.setSayAs(row.term, t));
              announce(t.trim() ? `${row.term} is said “${t.trim()}”` : `${row.term} has no pronunciation`);
            }}
          />
        ) : (
          <span className="say-as">{row.sayAs ?? ''}</span>
        )}
      </span>
      {row.note ? <span className="glossary-note">{row.note}</span> : null}
    </li>
  );
}

/** Add or change a glossary word: its kind, other spellings, how it's said and a note. */
function EntryEditor({ entry, isNew, sayAs: initialSay, live, onClose }: { entry: GlossaryEntry; isNew: boolean; sayAs: string; live: LiveProject | null; onClose: () => void }) {
  const [term, setTerm] = useState(entry.term);
  const [kind, setKind] = useState<GlossaryKind>(entry.kind);
  const [variants, setVariants] = useState(entry.variants.join(', '));
  const [note, setNote] = useState(entry.note);
  const [sayAs, setSayAs] = useState(initialSay);
  const [removing, setRemoving] = useState(false);
  const save = () => {
    if (!live || !term.trim()) return;
    const updated = glossaryEntry(term.trim(), kind, variants.split(',').map((v) => v.trim()).filter(Boolean), note.trim());
    storyEdit(live, (e) => {
      e.saveGlossaryEntry(updated, isNew ? undefined : entry);
      e.setSayAs(updated.term, sayAs);
    });
    announce(isNew ? `${updated.term} added to the glossary` : `${updated.term} saved`);
    onClose();
  };
  return (
    <StorySheet title={isNew ? 'Add Word' : entry.term} onClose={onClose} className="glossary-editor">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label className="stacked-label" htmlFor="glossary-term">
          Word or name
        </label>
        <input id="glossary-term" type="text" value={term} autoFocus autoComplete="off" spellCheck={false} onChange={(e) => setTerm(e.target.value)} />
        <label className="stacked-label" htmlFor="glossary-kind">
          Kind
        </label>
        <select id="glossary-kind" value={kind} onChange={(e) => setKind(e.target.value as GlossaryKind)}>
          {GLOSSARY_KINDS.map((k) => (
            <option key={k} value={k}>
              {GLOSSARY_KIND_LABELS[k]}
            </option>
          ))}
        </select>
        <label className="stacked-label" htmlFor="glossary-variants">
          Other spellings
        </label>
        <input id="glossary-variants" type="text" placeholder="Separate with commas" value={variants} spellCheck={false} onChange={(e) => setVariants(e.target.value)} />
        <p className="field-note">
          {kind === 'word' ? 'Spelled right everywhere in the project.' : 'Spelled right everywhere, and offered when you type @. Choosing it there adds it to Cast & Places.'}
        </p>
        <label className="stacked-label" htmlFor="glossary-say">
          Say as
        </label>
        <input id="glossary-say" type="text" placeholder="How the audiobook voice should say it" value={sayAs} spellCheck={false} onChange={(e) => setSayAs(e.target.value)} />
        <p className="field-note">A respelling the voice reads correctly, such as “ILL-suh” or “shiv-AWN”. Used by audiobooks and Read Aloud.</p>
        <label className="stacked-label" htmlFor="glossary-note">
          Note
        </label>
        <textarea id="glossary-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="actions">
          {!isNew ? (
            <button type="button" className="link-button danger" onClick={() => setRemoving(true)}>
              Remove from Glossary
            </button>
          ) : null}
          <span className="actions-spacer" />
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={!term.trim() || !live}>
            {isNew ? 'Add' : 'Done'}
          </button>
        </div>
      </form>
      {removing ? (
        <ConfirmDialog
          title={`Remove “${entry.term}”?`}
          message={<p>The spell checker will underline it again. Its pronunciation stays for audiobooks.</p>}
          confirm="Remove"
          onCancel={() => setRemoving(false)}
          onConfirm={() => {
            setRemoving(false);
            if (live) storyEdit(live, (e) => e.deleteGlossaryEntry(entry));
            announce(`${entry.term} removed from the glossary`);
            onClose();
          }}
        />
      ) : null}
    </StorySheet>
  );
}
